import React, { useEffect, useMemo, useState } from 'react';
import { Content } from '@carbon/react';
import { SideNav, SideNavItems, SideNavLink } from 'carbon-components-react';
import { useNavigate, useParams } from 'react-router-dom';
import AppHeader from '../appHeader';
import { defaultServiceTab, serviceTabOrder, settingsServiceMap, settingsTabs, SettingsTabId, ServiceTabId } from './settingsTypes';
import AccountSettingsTab from './tabs/AccountSettingsTab';
import GeneralSettingsTab from './tabs/GeneralSettingsTab';
import UserManagementTab from './tabs/UserManagementTab';
import ResourcesTab from './tabs/ResourcesTab';
import PermissionsTab from './tabs/PermissionsTab';
import ServicesTab from './tabs/ServicesTab';
import LicensesTab from './tabs/LicensesTab';
import ThirdpartyTab from './tabs/ThirdpartyTab';
import './SettingsPage.scss';
import { isServiceEnabledByBuild, isSettingsTabEnabledByBuild } from '../../config/build';
import { useAppSelector } from '../../app/hooks';
import { selectIsAdmin } from '../../AppSlice';

const isServiceTabId = (value?: string): value is ServiceTabId =>
  !!value && serviceTabOrder.includes(value as ServiceTabId);

const SettingsPage: React.FC = () => {
  const { tab, serviceId } = useParams<{ tab?: string; serviceId?: ServiceTabId }>();
  const navigate = useNavigate();
  const isAdmin = useAppSelector(selectIsAdmin);
  // Only admins can reach the configuration tabs; everyone keeps their account tab.
  const visibleSettingsTabs = useMemo(
    () => settingsTabs.filter((item) => isSettingsTabEnabledByBuild(item.id) && (isAdmin || item.id === 'account')),
    [isAdmin]
  );

  const visibleSettingsTabIds = useMemo(
    () => new Set(visibleSettingsTabs.map((item) => item.id)),
    [visibleSettingsTabs]
  );

  const defaultTab = useMemo<SettingsTabId>(() => {
    if (visibleSettingsTabIds.has('settings')) {
      return 'settings';
    }
    return visibleSettingsTabs[0]?.id ?? 'account';
  }, [visibleSettingsTabIds, visibleSettingsTabs]);

  useEffect(() => {
    if (!serviceId) return;
    if (!isServiceTabId(serviceId) || !visibleSettingsTabIds.has('services') || !isServiceEnabledByBuild(settingsServiceMap[serviceId])) {
      const targetPath = defaultTab === 'settings' ? '/settings' : `/settings/${defaultTab}`;
      navigate(targetPath, { replace: true });
    }
  }, [defaultTab, navigate, serviceId, visibleSettingsTabIds]);

  useEffect(() => {
    if (!tab) return;
    const matched = visibleSettingsTabs.some(item => item.id === tab);
    if (!matched && tab !== 'service') {
      const targetPath = defaultTab === 'settings' ? '/settings' : `/settings/${defaultTab}`;
      navigate(targetPath, { replace: true });
    }
  }, [defaultTab, navigate, tab, visibleSettingsTabs]);

  const resolvedTab: SettingsTabId = useMemo(() => {
    if (isServiceTabId(serviceId) && visibleSettingsTabIds.has('services') && isServiceEnabledByBuild(settingsServiceMap[serviceId])) {
      return 'services';
    }
    if (tab && visibleSettingsTabs.some(item => item.id === tab)) {
      return tab as SettingsTabId;
    }
    return defaultTab;
  }, [defaultTab, serviceId, tab, visibleSettingsTabIds, visibleSettingsTabs]);

  const [activeTab, setActiveTab] = useState<SettingsTabId>(resolvedTab);

  useEffect(() => {
    setActiveTab(resolvedTab);
  }, [resolvedTab]);

  const selectedServiceId: ServiceTabId = useMemo(() => {
    if (isServiceTabId(serviceId) && isServiceEnabledByBuild(settingsServiceMap[serviceId])) {
      return serviceId;
    }
    return defaultServiceTab;
  }, [serviceId]);

  const handleSideNavClick = (tabId: SettingsTabId) => {
    if (tabId === 'services') {
      const targetId = isServiceTabId(serviceId) ? serviceId : defaultServiceTab;
      setActiveTab('services');
      navigate(`/settings/service/${targetId}`);
    } else {
      setActiveTab(tabId);
      const targetPath = tabId === 'settings' ? '/settings' : `/settings/${tabId}`;
      navigate(targetPath);
    }
  };

  const renderPanel = () => {
    switch (activeTab) {
      case 'account':
        return <AccountSettingsTab />;
      case 'services':
        return (
          <ServicesTab
            selectedServiceId={selectedServiceId}
            onServiceChange={nextId => {
              setActiveTab('services');
              navigate(`/settings/service/${nextId}`);
            }}
          />
        );
      case 'users':
        return <UserManagementTab />;
      case 'resources':
        return <ResourcesTab />;
      case 'permissions':
        return <PermissionsTab />;
      case 'licenses':
        return <LicensesTab />;
      case 'thirdparty':
        return <ThirdpartyTab />;
      case 'settings':
      default:
        return <GeneralSettingsTab />;
    }
  };

  return (
    <div className="settings-page">
      <AppHeader hideSideNav titleOverride="Settings" activeService="home" />
      <SideNav
        aria-label="Settings navigation"
        isChildOfHeader={false}
        isFixedNav={false}
        expanded
        className="settings-page__sidenav"
        style={{ width: 300 }}
      >
        <SideNavItems>
          {visibleSettingsTabs.map(navTab => (
            <SideNavLink
              key={navTab.id}
              href="#"
              className={`settings-page__sidenav-link ${navTab.id === activeTab ? 'settings-page__sidenav-link--active' : ''}`}
              isActive={navTab.id === activeTab}
              onClick={e => {
                e.preventDefault();
                handleSideNavClick(navTab.id);
              }}
            >
              <span className="settings-page__tab-title">{navTab.label}</span>
            </SideNavLink>
          ))}
        </SideNavItems>
      </SideNav>

      <Content className="settings-page__body">
        <div style={{ marginLeft: 220 }}>
          {renderPanel()}
        </div>
      </Content>
    </div>
  );
};

export default SettingsPage;
